package ui.app.assign

import androidx.compose.runtime.*
import androidx.compose.ui.text.input.TextFieldValue
import api.AppType
import common.frontend.api.Io
import common.frontend.api.canLayerBottomAdd
import io.Repository
import kotlinx.coroutines.launch
import tk.mallumo.compose.navigation.navTo_AppDetailUI
import tk.mallumo.compose.navigation.navTo_AppEditorWheelUI
import tk.mallumo.compose.navigation.navTo_AppKindergartenEditorUI
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.findRoot
import tools.printStackTraceInDebug
import ui.app.assign.detail.AppDetailArgs
import ui.snackBarStateGlobal

@VM
class AppAssignListingVM : NavigationViewModel() {

    private lateinit var scopeUi: AppAssignListingScope

    var state by mutableDataState<AppAssignListingScope.Wrapper>()
        private set

    var variants by mutableStateOf<List<Io.ListingAppVariant.Item>>(listOf())
        private set

    val searchFiled = mutableStateOf(TextFieldValue(""))


    val tabAll by lazy {
        listOf(
            AppAssignListingScope.Tab.Active,
            AppAssignListingScope.Tab.Unused,
            AppAssignListingScope.Tab.Archived
        )
    }

    var tabActive by mutableStateOf<AppAssignListingScope.Tab>(AppAssignListingScope.Tab.Active)
        private set

    sealed interface Action {
        data class CreateApp(val variant: Io.ListingAppVariant.Item) : Action
        data class ActivateTab(val tab: AppAssignListingScope.Tab) : Action
        companion object {

            @Composable
            fun rememberAction(vm: AppAssignListingVM): (Action) -> Unit {
                return remember {
                    { act ->
                        when (act) {
                            is CreateApp -> vm.createApp(act.variant)
                            is ActivateTab -> vm.tabActive = act.tab
                        }
                    }
                }
            }
        }
    }

    private fun createApp(variant: Io.ListingAppVariant.Item) {
        scope.launch {
            runCatching {
                Repository.api.app.add(
                    companyId = scopeUi.args.companyId,
                    variantId = variant.id,
                    lang = scopeUi.lang
                )
            }.onSuccess {
                with(scopeUi.nav.findRoot()) {
                    val arg = AppDetailArgs(scopeUi.args.companyId, it)
                    when (AppType[variant.type]) {
                        AppType.WHEEL -> navTo_AppEditorWheelUI(arg)
                        AppType.KINDERGARTEN -> navTo_AppKindergartenEditorUI(arg)
                        AppType.APP_GROUP,
                        AppType.BALOON,
                        AppType.CAMERA,
                        AppType.GALLERY,
                        AppType.IMAGE,
                        AppType.MAZE,
                        AppType.NONE,
                        AppType.PDF,
                        AppType.QUIZ,
                        AppType.RSS,
                        AppType.USB_PLAYER,
                        AppType.VIDEO,
                        AppType.VIDEO_ALPHA_LOOP,
                        AppType.WWW_EMBEDDED,
                        AppType.WWW_LINK -> navTo_AppDetailUI(arg)
                    }
                }
            }.onFailure {
                it.printStackTraceInDebug()
                snackBarStateGlobal.showSnackbar(
                    it.message!!,
                    withDismissAction = true
                )
            }
        }
    }

    fun reloadVariants(companyId: Long) {
        scope.launch {
            runCatching {
                variants = Repository.api.app.listingVariant(companyId).items
            }
        }
    }

    fun init(scopeUi: AppAssignListingScope) {
        this.scopeUi = scopeUi
        if (state !is DataState.Result) {
            state = DataState.Loading()
        }

        scope.launch {
            runCatching {
                variants = Repository.api.app.listingVariant(scopeUi.args.companyId).items
                val data = Repository.api.app.listingApps(scopeUi.args.companyId)
                state = DataState.Result(
                    AppAssignListingScope.Wrapper(
                        canAdd = scopeUi.login.canLayerBottomAdd(scopeUi.args.companyId),
                        origin = data
                    )
                )
            }.onFailure {
                it.printStackTraceInDebug()
                state = DataState.Error(it.message!!, throwable = it)
            }
        }
    }

    override fun onRelease() {
        state.entry?.release()
        state = DataState.Idle()
    }

}
